import { type FC, memo, useCallback, useLayoutEffect, useRef } from 'react';

import { EVENT_TYPE } from '@~/constants/logger';
import { useAppSelector } from '@~/hooks';
import usePrevious from '@~/hooks/usePrevious';
import analytics from '@~/modules/analytics';
import useStudentHomeDetails from '@~/hooks/use-student-home-details';

// note: do not remove these are used in group analytics for mixpanel
const GROUP_ANALYTICS_PARENT_KEY = 'parent_id';
const GROUP_ANALYTICS_STUDENT_KEY = 'student_service_id';
const STUDENT_ENROLMENT_STATUS = 'student_enrolment_status';

const AuthTransitions: FC = memo(function AuthTransitions() {
  const { studentServiceId } = useAppSelector(str => str.auth);
  const prevStudentServiceId = usePrevious(studentServiceId);
  const isFirstTimeRenderRef = useRef(true);
  const { extra_fields: extraFields, student } = useStudentHomeDetails();

  const parentId = extraFields?.parent_id;

  const setStudentSuperAttributes = useCallback(() => {
    analytics.setSuperAttribute(GROUP_ANALYTICS_STUDENT_KEY, studentServiceId);

    if (student?.enrollment_category) {
      analytics.setSuperAttribute(
        STUDENT_ENROLMENT_STATUS,
        student.enrollment_category?.toLowerCase(),
      );
    }
  }, [student?.enrollment_category, studentServiceId]);

  const deleteStudentSuperAttributes = useCallback(() => {
    analytics.deleteSuperAttribute(GROUP_ANALYTICS_STUDENT_KEY);
    analytics.deleteSuperAttribute(STUDENT_ENROLMENT_STATUS);
  }, []);

  useLayoutEffect(() => {
    if (!prevStudentServiceId && studentServiceId && !isFirstTimeRenderRef.current) {
      analytics.login(studentServiceId, 'STUDENT');
      setStudentSuperAttributes();

      analytics.trackEvent(EVENT_TYPE.STUDENT_LOGGED_IN);
    }

    if (prevStudentServiceId && !studentServiceId) {
      analytics.deleteSuperAttribute(GROUP_ANALYTICS_PARENT_KEY);
      deleteStudentSuperAttributes();

      analytics.trackEvent(EVENT_TYPE.STUDENT_LOGGED_OUT);
      analytics.logout();
    }

    isFirstTimeRenderRef.current = false;
  }, [
    deleteStudentSuperAttributes,
    prevStudentServiceId,
    setStudentSuperAttributes,
    studentServiceId,
  ]);

  useLayoutEffect(() => {
    if (parentId) {
      analytics.setSuperAttribute(GROUP_ANALYTICS_PARENT_KEY, parentId);
    } else {
      analytics.deleteSuperAttribute(GROUP_ANALYTICS_PARENT_KEY);
    }

    if (studentServiceId) {
      setStudentSuperAttributes();
    } else {
      deleteStudentSuperAttributes();
    }
  }, [
    deleteStudentSuperAttributes,
    parentId,
    setStudentSuperAttributes,
    studentServiceId,
  ]);

  return null;
});

export default AuthTransitions;
